import type {
  GetAnnouncementsFilters,
  ApiAnnouncementResponseType,
  CreateAnnouncementType,
  UpdateAnnouncementType,
  AnnouncementType,
} from "@/types/entities/announcements";
import axios from "@/utils/axios";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

export const useGetAnnouncements = (filters: GetAnnouncementsFilters = {}) => {
  return useQuery({
    queryKey: ["announcements", filters],
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<ApiAnnouncementResponseType> => {
      const response = await axios.get("/admin/v1/announcements", {
        params: {
          ...filters,
        },
      });
      return response.data;
    },
  });
};

export const useGetActiveAnnouncements = () => {
  return useQuery({
    queryKey: ["announcements", "active"],
    queryFn: async (): Promise<{ data: AnnouncementType[] }> => {
      const response = await axios.get("/v1/announcements");
      return response.data;
    },
  });
};

export const useGetAnnouncement = (id: string) => {
  return useQuery({
    queryKey: ["announcement", id],
    queryFn: async (): Promise<{ data: AnnouncementType }> => {
      const response = await axios.get(`/admin/v1/announcements/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useCreateAnnouncement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateAnnouncementType) => {
      const response = await axios.post("/admin/v1/announcements", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["announcements"],
      });
    },
  });
};

export const useUpdateAnnouncement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpdateAnnouncementType) => {
      const response = await axios.put(
        `/admin/v1/announcements/${data.id}`,
        data,
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["announcements"],
      });
      queryClient.invalidateQueries({
        queryKey: ["announcement", variables.id],
      });
    },
  });
};

export const useDeleteAnnouncement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete(`/admin/v1/announcements/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["announcements"],
      });
    },
  });
};

export const useToggleAnnouncementStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      action,
    }: {
      id: string;
      action: "publish" | "unpublish";
    }) => {
      const response = await axios.post(
        `/admin/v1/announcements/${id}/status/${action}`,
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["announcements"],
      });
      queryClient.invalidateQueries({
        queryKey: ["announcement", variables.id],
      });
    },
  });
};
